/**
 * **걷기 스트립 배치 계산** — 어디에 얼마로 놓을지. 픽셀은 안 만지고 숫자만 낸다.
 *
 * ## 왜 여기 있나
 * 같은 계산을 두 곳이 한다:
 *  - `scripts/grid-to-walk-strip.cjs` (개발자, sharp)
 *  - 관리자 「움직임 만들기」 (운영자, 브라우저 canvas)
 *
 * 각자 짜면 **관리자에서 만든 것과 개발자가 만든 것이 미묘하게 달라진다** —
 * 그러면 그 방향만 키가 다르거나 발이 뜨고, 화면에서야 알아챈다
 * (CLAUDE.md — 미리보기를 만드는 화면은 렌더러와 같은 판정 함수를 쓴다).
 *
 * 그래서 **자르기·배경 제거는 각자 하고**(도구가 다르다), **배치 숫자는 여기서만** 낸다.
 */

/** 잘라낸 한 칸의 인물 크기(여백을 뗀 뒤). */
export interface CellSize {
  width: number;
  height: number;
}

export interface PlacementInput {
  /** 칸 4개의 인물 크기(여백 제거 후). */
  cells: CellSize[];
  /** 프레임 한 칸 크기(웹 규약 128). */
  frame: number;
  /**
   * 기존 스트립에서 잰 값. 있으면 **그 키·발 위치를 그대로** 쓴다.
   *
   * 캐릭터군마다 프레임을 채우는 비율이 다르다 — 히어로 룩은 0.97, 마을 친구는 0.88.
   * 이걸 안 쓰면 새로 만든 방향만 키가 10% 작아진다.
   */
  ref?: { bodyH: number; footMargin: number };
  /** ref 가 없을 때 쓸 기본값. */
  fallback?: { maxHRatio: number; footMargin: number };
  /**
   * 칸마다 다른 크기를 **같은 키로 맞출지**.
   *
   * 사람이 그린 그림은 자세 때문에 조금 다른 게 자연스럽다(기본 false).
   * 생성기가 뽑은 것은 규칙 없이 흔들려 **캐릭터가 커졌다 작아졌다** 하므로 true.
   */
  lockHeight?: boolean;
}

export interface Placement {
  /** 칸별 최종 크기·위치(프레임 좌상단 기준). */
  frames: Array<{ width: number; height: number; left: number; top: number }>;
  /** 스트립 전체 크기. */
  stripWidth: number;
  stripHeight: number;
  /** 공통 배율(참고용 로그). */
  scale: number;
  /** 칸마다 키가 얼마나 다른가(0~1). 4% 넘으면 걸을 때 티가 난다. */
  wobble: number;
  footMargin: number;
}

const DEFAULTS = { maxHRatio: 0.97, footMargin: 2 };

/**
 * 칸 크기들 → 스트립 배치.
 *
 * 가로는 가운데, 세로는 **발끝을 바닥선에 맞춘다** — 이게 안 맞으면 걸을 때 위아래로 튄다.
 */
export function planWalkStrip(input: PlacementInput): Placement {
  const { cells, frame } = input;
  const fb = { ...DEFAULTS, ...input.fallback };
  const tallest = Math.max(1, ...cells.map((c) => c.height));
  const shortest = Math.min(...cells.map((c) => c.height));
  const wobble = tallest > 0 ? (tallest - shortest) / tallest : 0;

  const scale = input.ref ? input.ref.bodyH / tallest : (frame * fb.maxHRatio) / tallest;
  const footMargin = input.ref ? input.ref.footMargin : fb.footMargin;

  const frames = cells.map((c, i) => {
    // lockHeight: 이 칸만의 배율을 더 줘서 **모든 칸을 같은 키**로 만든다
    const s = input.lockHeight ? scale * (tallest / Math.max(1, c.height)) : scale;
    const width = Math.max(1, Math.round(c.width * s));
    const height = Math.max(1, Math.round(c.height * s));
    return {
      width,
      height,
      left: i * frame + Math.round((frame - width) / 2), // 가로 중앙
      top: frame - footMargin - height, // 발끝을 바닥선에
    };
  });

  return {
    frames,
    stripWidth: frame * cells.length,
    stripHeight: frame,
    scale,
    wobble,
    footMargin,
  };
}

/** 2×2 격자를 읽는 순서 = 걷기 순서. 0번이 서 있기(웹 규약). */
export function gridCells(width: number, height: number): Array<{ left: number; top: number; width: number; height: number }> {
  const w = Math.floor(width / 2);
  const h = Math.floor(height / 2);
  return [
    { left: 0, top: 0, width: w, height: h },
    { left: w, top: 0, width: w, height: h },
    { left: 0, top: h, width: w, height: h },
    { left: w, top: h, width: w, height: h },
  ];
}

/** 4% 넘게 흔들리면 걸을 때 티가 난다 — 화면·콘솔에 같은 기준으로 경고하려고 여기 둔다. */
export const WOBBLE_WARN = 0.04;
